import React from 'react';
import _entries from 'lodash/entries';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _map from 'lodash/map';
import _noop from 'lodash/noop';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import { Button, KIND, SIZE } from 'baseui/button';
import { StyledLink } from 'baseui/link';

import { EMPTY_STRING } from '../../constants';
import copy from '../../assets/misc/copy.png';
import Loader from '../Loader';
import DataList from '../baselist/DataList';

import { KeyConfig, KeyValueViewProps } from './types';
import Accordian from './Accordian';
import useKeyValueRenderer from './useKeyValueRenderer';
import KeyValueFilter from './KeyValueFilter';
import useStyles from './useStyles';
import { FILTER_LAYOUT } from './constant';

const KeyValueRenderer = (props: KeyValueViewProps) => {
	const { keyConfig, forceUpdate, turnForceUpdateOff, turnForceUpdateOn, keyValueLayout } = props;
	const getActionHandler = _get(props, 'getActionHandler', _noop);
	const getActionList = _get(props, 'getActionList', _noop);
	const styleOverride = _get(keyConfig, ['override', 'style']);

	const keyActions = getActionList({ keyConfig });

	const {
		handleFilterChange,
		getChildData,
		keyData,
		childForceUpdate,
		turnForceUpdateOffForChild,
		turnForceUpdateOnForChild,
		setIsCollapsed,
		isCollapsed,
		showLoader,
		copyToClipboard,
		handleForceUpdate,
		setKeyData
	} = useKeyValueRenderer({ keyConfig, forceUpdate, turnForceUpdateOff, turnForceUpdateOn });
	const filterConfig = _get(keyConfig, 'filterConfig');
	const label = _get(keyConfig, 'label');
	const classes = useStyles({ keyValueLayout, styleOverride, keyData });
	const getFilterLayoutPosition = () => {
		return _get(filterConfig, ['layoutPosition'], FILTER_LAYOUT.inLabel);
	};

	const labelJSX = (
		<div className={classes.labelWithFilter}>
			<div className={classes.labelText}>{label}</div>
			{!_isNil(label) && filterConfig && !_isEmpty(filterConfig) && getFilterLayoutPosition() === FILTER_LAYOUT.inLabel && (
				<KeyValueFilter filterConfig={filterConfig} onSubmit={handleFilterChange} />
			)}
			<div title='click to copy'>
				<Button kind={KIND.tertiary} onClick={copyToClipboard}>
					<img className={classes.ctcImage} alt='copy_to_clipboard' src={copy} />
					<div className={classes.ctcText}>Click To Copy</div>
				</Button>
			</div>
		</div>
	);

	const renderValue = (value: any) => {
		const buttonId = _get(keyConfig, ['override', 'buttonId']);
		const actionHandler = buttonId ? getActionHandler(buttonId, { handleForceUpdate, keyData: value, setKeyData }) : _noop;
		if (_isNil(value)) {
			return '-';
		}
		const format = _get(keyConfig, ['override', 'format'], 'string');

		switch (format) {
			case 'json': {
				const jsonKeyLabelMap = _get(keyConfig, ['override', 'jsonKeyLabelMap'], '');
				const isPayoutDistribution = _get(keyConfig, ['override', 'isPayoutDistribution'], false);
				return (
					<div className={classes.jsonTextWrapper}>
						{_map(_entries(value), ([jsonKey, jsonValue]: [string, string]) => (
							<div className={classes.jsonTextPairWrapper}>
								<div>{`${isPayoutDistribution ? jsonKey : jsonKeyLabelMap[jsonKey]} :`}</div>
								<div>{JSON.stringify(jsonValue)}</div>
							</div>
						))}
					</div>
				);
			}
			case 'image': {
				return (
					<a href={value} target='_blank' rel='noreferrer'>
						<img className={classes.documentImage} src={value} alt={value} />
					</a>
				);
			}
			case 'button': {
				return (
					<Button kind={KIND.tertiary} onClick={actionHandler}>
						<StyledLink>{value}</StyledLink>
					</Button>
				);
			}
			default:
				if (typeof value === 'object') {
					return JSON.stringify(value);
				}

				return value;
		}
	};

	const keyValueListJSX = (
		<>
			{showLoader && <Loader />}
			<div className={classes.listFilterContainer}>
				{!_isNil(label) && filterConfig && !_isEmpty(filterConfig) && getFilterLayoutPosition() === FILTER_LAYOUT.top && (
					<KeyValueFilter filterConfig={filterConfig} onSubmit={handleFilterChange} />
				)}
				<div className={classes.keyValueList}>
					{_map(_get(keyConfig, 'childConfig'), (key: KeyConfig) => {
						const { name: childName, isHidden } = key;
						return (
							<>
								{!isHidden && (
									<KeyValueRenderer
										keyConfig={{ ...key, fetchData: _get(key, 'fetchData', getChildData(childName)) }}
										forceUpdate={_get(childForceUpdate, childName)}
										key={childName}
										turnForceUpdateOff={turnForceUpdateOffForChild(childName)}
										turnForceUpdateOn={turnForceUpdateOnForChild(childName)}
										keyValueLayout={keyValueLayout}
										getActionHandler={getActionHandler}
										getActionList={getActionList}
									/>
								)}
							</>
						);
					})}
				</div>
				{!_isNil(label) && filterConfig && !_isEmpty(filterConfig) && getFilterLayoutPosition() === FILTER_LAYOUT.bottom && (
					<KeyValueFilter filterConfig={filterConfig} onSubmit={handleFilterChange} />
				)}
			</div>
		</>
	);

	const ActionButtonJSX = (
		<>
			{_map(keyActions, (action) => {
				const actionHandler = getActionHandler(_get(action, 'id'), { handleForceUpdate, keyData, setKeyData });
				const buttonLabel = _get(action, 'label', EMPTY_STRING);
				const isEnabled = _get(action, 'isEnabled', _noop);

				return (
					isEnabled({ keyData }) && (
						<Button onClick={actionHandler} size={SIZE.mini}>
							{buttonLabel}
						</Button>
					)
				);
			})}
		</>
	);

	return (
		<>
			{!_has(keyConfig, 'childConfig') && _get(keyConfig, ['override', 'format']) !== 'datalist' ? (
				<div className={classes.keyValueRow}>
					<div className={classes.key}>{label}</div>
					<div className={classes.valueContainer}>
						<div className={classes.value}>{renderValue(keyData)}</div>
						{ActionButtonJSX}
					</div>
				</div>
			) : (
				<>
					{_isNil(label) ? (
						<>{keyValueListJSX}</>
					) : (
						<Accordian
							label={labelJSX}
							panelKey={label || 'unnamedPanel'}
							isCollapsed={isCollapsed}
							setIsCollapsed={setIsCollapsed}>
							{_get(keyConfig, ['override', 'format']) === 'datalist' ? (
								<DataList {..._get(keyConfig, ['override', 'datalistProps'], {})} />
							) : (
								keyValueListJSX
							)}
						</Accordian>
					)}
				</>
			)}
		</>
	);
};

export default KeyValueRenderer;
